import { useState, useRef, useEffect } from 'react';
import api from '../api/client';
import { useAuthStore } from '../store/auth';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function AiPage() {
  const plan = useAuthStore((s) => s.tenant?.plan);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  if (plan !== 'GOLD') {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <div className="text-center max-w-sm animate-scale-in">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-surface-100 to-surface-200 flex items-center justify-center mx-auto mb-5 shadow-soft">
            <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">KI-Assistent</h2>
          <p className="text-sm text-slate-400 leading-relaxed">
            Dieses Feature ist nur im Gold-Plan verfügbar.<br />
            Kontaktieren Sie uns unter info@samoplan.de
          </p>
        </div>
      </div>
    );
  }

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg: Message = { role: 'user', content: input.trim() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);
    try {
      const { data } = await api.post('/ai/chat', { messages: updated });
      setMessages([...updated, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages([...updated, { role: 'assistant', content: 'Fehler bei der KI-Anfrage.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <div className="sp-page-header !mb-5">
        <div>
          <h1 className="sp-page-title">KI-Assistent</h1>
          <p className="sp-page-subtitle">Claude unterstützt Sie bei Angeboten, Kalkulation und mehr</p>
        </div>
        {messages.length > 0 && (
          <button onClick={() => setMessages([])} className="sp-btn-secondary text-xs">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Neuer Chat
          </button>
        )}
      </div>

      {/* Chat */}
      <div className="flex-1 sp-card overflow-y-auto mb-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full animate-fade-in px-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center mb-5 shadow-glow">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1">Wie kann ich Ihnen helfen?</h3>
            <p className="text-sm text-slate-400 max-w-md text-center leading-relaxed mb-6">
              Ich unterstütze Sie bei Angebotspositionen, Kalkulation, Baudokumentation und vielem mehr.
            </p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg">
              {[
                'Angebotspositionen für Dachsanierung generieren',
                'Eingangsrechnung prüfen',
                'Baustellenbericht formulieren',
                'Kalkulation überprüfen',
              ].map((s) => (
                <button key={s} onClick={() => setInput(s)}
                  className="px-4 py-2.5 bg-surface-50 border border-surface-200 text-sm text-slate-600 rounded-xl hover:bg-surface-100 hover:border-surface-300 hover:shadow-soft transition-all duration-200">
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                {m.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-[10px] font-bold text-white mr-3 mt-1 flex-shrink-0 shadow-sm">
                    KI
                  </div>
                )}
                <div className={`max-w-[70%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-brand-500 text-white rounded-br-md shadow-sm'
                    : 'bg-surface-50 text-slate-700 rounded-bl-md border border-surface-200'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start animate-fade-in">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-[10px] font-bold text-white mr-3 mt-1 flex-shrink-0 shadow-sm">
                  KI
                </div>
                <div className="bg-surface-50 border border-surface-200 rounded-2xl rounded-bl-md px-5 py-4">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse-dot" />
                    <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse-dot" style={{ animationDelay: '200ms' }} />
                    <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse-dot" style={{ animationDelay: '400ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder="Nachricht eingeben..."
            className="sp-input pr-12"
            disabled={loading}
          />
        </div>
        <button onClick={send} disabled={loading || !input.trim()} className="sp-btn-primary px-5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>
    </div>
  );
}
